import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { projectTemplateService } from '../services/projectTemplateService'
import { 
  ProjectTemplate, 
  CreateProjectTemplateData, 
  ProjectPhase,
  projectTemplateCategories
} from '../types/projectTemplate'
import AppHeader from '../components/AppHeader'
import NotificationDropdown from '../components/NotificationDropdown'
import SettingsDropdown from '../components/SettingsDropdown'
import ProfileDropdown from '../components/ProfileDropdown'
import { 
  PlusIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  DocumentDuplicateIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  StarIcon,
  ClockIcon,
  UsersIcon,
  TagIcon,
  CalendarIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'

export default function ProjectTemplatesPage() {
  const { user } = useAuth()
  const { showSuccess, showError } = useNotification()
  
  const [templates, setTemplates] = useState<ProjectTemplate[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<ProjectTemplate | null>(null)
  const [viewingTemplate, setViewingTemplate] = useState<ProjectTemplate | null>(null)
  const [usingTemplate, setUsingTemplate] = useState<ProjectTemplate | null>(null)

  useEffect(() => {
    if (user) {
      loadTemplates()
    }
  }, [user, selectedCategory])

  const loadTemplates = async () => {
    try {
      setIsLoading(true)
      const category = selectedCategory === 'all' ? undefined : selectedCategory as any
      const result = await projectTemplateService.getProjectTemplates(user!.id, category, true)
      setTemplates(result)
    } catch (error) {
      console.error('Error loading templates:', error)
      showError('Error', 'Failed to load project templates')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateTemplate = async (templateData: CreateProjectTemplateData) => {
    try {
      const result = await projectTemplateService.createProjectTemplate(user!.id, templateData)
      if (result.success && result.template) {
        setTemplates(prev => [result.template!, ...prev])
        setShowCreateModal(false)
        showSuccess('Success', 'Project template created successfully')
      } else {
        showError('Error', result.error || 'Failed to create template')
      }
    } catch (error) {
      console.error('Error creating template:', error)
      showError('Error', 'Failed to create template')
    }
  }

  const handleDuplicateTemplate = async (template: ProjectTemplate) => {
    try {
      const duplicateData: CreateProjectTemplateData = {
        name: `${template.name} (Copy)`,
        description: template.description,
        category: template.category,
        subcategory: template.subcategory,
        estimatedDuration: template.estimatedDuration,
        recommendedTeamSize: template.recommendedTeamSize,
        difficultyLevel: template.difficultyLevel,
        phases: template.phases.map(phase => ({
          name: phase.name,
          description: phase.description,
          order: phase.order,
          estimatedDays: phase.estimatedDays,
          color: phase.color,
          prerequisites: phase.prerequisites,
          deliverables: phase.deliverables,
          tasks: phase.tasks
        })),
        tasks: template.tasks.map(task => ({
          title: task.title,
          description: task.description,
          category: task.category,
          estimatedHours: task.estimatedHours,
          priority: task.priority,
          phaseId: task.phaseId,
          order: task.order,
          dependsOn: task.dependsOn,
          assignedTo: task.assignedTo,
          skillsRequired: task.skillsRequired,
          daysBeforeShoot: task.daysBeforeShoot,
          daysAfterPrevious: task.daysAfterPrevious,
          isAutomated: task.isAutomated,
          automationTrigger: task.automationTrigger,
          resources: task.resources,
          notes: task.notes
        })),
        deliverables: template.deliverables.map(deliverable => ({
          name: deliverable.name,
          description: deliverable.description,
          type: deliverable.type,
          format: deliverable.format,
          quality: deliverable.quality,
          quantity: deliverable.quantity,
          estimatedDeliveryDays: deliverable.estimatedDeliveryDays,
          phaseId: deliverable.phaseId,
          requiresClientApproval: deliverable.requiresClientApproval,
          allowsClientRevisions: deliverable.allowsClientRevisions,
          maxRevisions: deliverable.maxRevisions,
          isIncludedInBase: deliverable.isIncludedInBase,
          additionalCost: deliverable.additionalCost
        })),
        suggestedPricing: template.suggestedPricing,
        tags: [...template.tags],
        isPublic: false
      }
      
      await handleCreateTemplate(duplicateData)
    } catch (error) {
      console.error('Error duplicating template:', error)
      showError('Error', 'Failed to duplicate template')
    }
  }

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      const result = await projectTemplateService.deleteProjectTemplate(templateId)
      if (result.success) {
        setTemplates(prev => prev.filter(t => t.id !== templateId))
        showSuccess('Success', 'Template deleted successfully')
      } else {
        showError('Error', result.error || 'Failed to delete template')
      }
    } catch (error) {
      console.error('Error deleting template:', error)
      showError('Error', 'Failed to delete template')
    }
  }

  const handleUseTemplate = async (projectData: {
    name: string
    clientId?: string
    shootId?: string
    startDate: string
    shootDate?: string
  }) => {
    if (!usingTemplate) return
    
    try {
      const result = await projectTemplateService.createProjectFromTemplate(
        user!.id, 
        usingTemplate.id, 
        projectData
      )
      
      if (result.success && result.project) {
        setUsingTemplate(null)
        showSuccess('Success', 'Project created from template successfully')
        // Optionally redirect to project page or dashboard
      } else {
        showError('Error', result.error || 'Failed to create project from template')
      }
    } catch (error) {
      console.error('Error creating project from template:', error)
      showError('Error', 'Failed to create project from template')
    }
  }

  const filteredTemplates = templates.filter(template => 
    template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  )

  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'beginner': return 'text-green-600 bg-green-100'
      case 'intermediate': return 'text-yellow-600 bg-yellow-100'
      case 'advanced': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getCategoryIcon = (category: string) => {
    return projectTemplateCategories[category as keyof typeof projectTemplateCategories]?.icon || '📝'
  }

  if (isLoading) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        {/* Floating orbs for glassmorphism background effect */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="floating-orb floating-orb-1"></div>
          <div className="floating-orb floating-orb-2"></div>
          <div className="floating-orb floating-orb-3"></div>
          <div className="floating-orb floating-orb-4"></div>
          <div className="floating-orb floating-orb-5"></div>
        </div>

        {/* Header */}
        <AppHeader>
          <NotificationDropdown />
          <SettingsDropdown />
          <ProfileDropdown />
        </AppHeader>

        <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-80px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Floating orbs for glassmorphism background effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <AppHeader>
        <NotificationDropdown />
        <SettingsDropdown />
        <ProfileDropdown />
      </AppHeader>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Project Templates</h1>
              <p className="text-gray-400">Streamline your workflow with reusable project templates</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              New Template
            </button>
          </div>
        </motion.div>

        {/* Filters and Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-mockup rounded-xl border border-white/10 p-6 mb-6"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-3 py-3 glass-mockup rounded-lg border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent w-full"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-4 w-4 text-gray-400" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="glass-mockup border border-white/10 rounded-lg px-3 py-3 text-white bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
              >
                <option value="all" className="bg-gray-900 text-white">All Categories</option>
                {Object.entries(projectTemplateCategories).map(([key, category]) => (
                  <option key={key} value={key} className="bg-gray-900 text-white">
                    {category.icon} {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </motion.div>

        {/* Templates Grid */}
        {filteredTemplates.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-mockup rounded-xl border border-white/10 p-12 text-center"
          >
            <DocumentDuplicateIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No templates found</h3>
            <p className="text-gray-400 mb-6">
              {searchQuery || selectedCategory !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'Get started by creating your first project template'
              }
            </p>
            {!searchQuery && selectedCategory === 'all' && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Create Template
              </button>
            )}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredTemplates.map((template, index) => (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.05 }}
                className="glass-mockup rounded-xl border border-white/10 hover:border-white/20 transition-all p-6 hover:scale-105"
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="text-2xl">
                        {getCategoryIcon(template.category)}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-white mb-1">{template.name}</h3>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(template.difficultyLevel)}`}>
                            {template.difficultyLevel}
                          </span>
                          {template.isDefault && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
                              Default
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions Dropdown */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setUsingTemplate(template)}
                        className="p-1 text-blue-400 hover:text-blue-300 transition-colors"
                        title="Use Template"
                      >
                        <PlusIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setViewingTemplate(template)}
                        className="p-1 text-gray-400 hover:text-white transition-colors"
                        title="View Details"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDuplicateTemplate(template)}
                        className="p-1 text-gray-400 hover:text-white transition-colors"
                        title="Duplicate"
                      >
                        <DocumentDuplicateIcon className="w-4 h-4" />
                      </button>
                      {template.userId === user?.id && !template.isDefault && (
                        <>
                          <button
                            onClick={() => setEditingTemplate(template)}
                            className="p-1 text-gray-400 hover:text-white transition-colors"
                            title="Edit"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteTemplate(template.id)}
                            className="p-1 text-gray-400 hover:text-red-400 transition-colors"
                            title="Delete"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">{template.description}</p>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
                    <div className="flex items-center gap-1 text-gray-400">
                      <CalendarIcon className="w-4 h-4" />
                      <span>{template.estimatedDuration}d</span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-400">
                      <UsersIcon className="w-4 h-4" />
                      <span>{template.recommendedTeamSize}</span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-400">
                      <StarIcon className="w-4 h-4" />
                      <span>{template.usageCount}</span>
                    </div>
                  </div>

                  {/* Phases */}
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <ClockIcon className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-300">
                        {template.phases.length} Phases
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {template.phases.slice(0, 3).map((phase) => (
                        <span
                          key={phase.id}
                          className="px-2 py-1 text-xs rounded"
                          style={{ backgroundColor: `${phase.color}20`, color: phase.color }}
                        >
                          {phase.name}
                        </span>
                      ))}
                      {template.phases.length > 3 && (
                        <span className="px-2 py-1 bg-white/10 text-gray-300 text-xs rounded">
                          +{template.phases.length - 3}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Tags */}
                  {template.tags.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <TagIcon className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-300">Tags</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {template.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded">
                            {tag}
                          </span>
                        ))}
                        {template.tags.length > 3 && (
                          <span className="px-2 py-1 bg-white/10 text-gray-300 text-xs rounded">
                            +{template.tags.length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Pricing */}
                  {template.suggestedPricing && (
                    <div className="pt-4 border-t border-white/10">
                      <div className="text-sm text-gray-400">
                        Suggested: <span className="font-medium text-white">
                          ${template.suggestedPricing.min.toLocaleString()} - ${template.suggestedPricing.max.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Modals */}
        {showCreateModal && (
          <CreateTemplateModal
            onClose={() => setShowCreateModal(false)}
            onSave={handleCreateTemplate}
          />
        )}

        {editingTemplate && (
          <EditTemplateModal
            template={editingTemplate}
            onClose={() => setEditingTemplate(null)}
            onSave={(updatedTemplate: ProjectTemplate) => {
              setTemplates(prev => prev.map(t => t.id === updatedTemplate.id ? updatedTemplate : t))
              setEditingTemplate(null)
              showSuccess('Success', 'Template updated successfully')
            }}
          />
        )}

        {viewingTemplate && (
          <ViewTemplateModal
            template={viewingTemplate}
            onClose={() => setViewingTemplate(null)}
            onEdit={() => {
              setEditingTemplate(viewingTemplate)
              setViewingTemplate(null)
            }}
            onDuplicate={() => {
              handleDuplicateTemplate(viewingTemplate)
              setViewingTemplate(null)
            }}
          />
        )}

        {usingTemplate && (
          <UseTemplateModal
            template={usingTemplate}
            onClose={() => setUsingTemplate(null)}
            onUse={handleUseTemplate}
          />
        )}
      </main>
    </div>
  )
}

// Create Template Modal Component
function CreateTemplateModal({ 
  onClose, 
  onSave 
}: { 
  onClose: () => void
  onSave: (template: CreateProjectTemplateData) => void 
}) {
  const [formData, setFormData] = useState<CreateProjectTemplateData>({
    name: '',
    description: '',
    category: 'portrait',
    subcategory: '',
    estimatedDuration: 7,
    recommendedTeamSize: 1,
    difficultyLevel: 'intermediate',
    phases: [],
    tasks: [],
    deliverables: [],
    tags: [],
    isPublic: false
  })

  const [currentPhase, setCurrentPhase] = useState<Omit<ProjectPhase, 'id'>>({
    name: '',
    description: '',
    order: 1,
    estimatedDays: 1,
    color: '#3b82f6',
    prerequisites: [],
    deliverables: [],
    tasks: []
  })

  const [showPhaseForm, setShowPhaseForm] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim()) return
    
    onSave(formData)
  }

  const addPhase = () => {
    if (!currentPhase.name.trim()) return
    
    setFormData(prev => ({
      ...prev,
      phases: [...prev.phases, { ...currentPhase, order: prev.phases.length + 1 }]
    }))
    
    setCurrentPhase({
      name: '',
      description: '',
      order: formData.phases.length + 2,
      estimatedDays: 1,
      color: '#3b82f6',
      prerequisites: [],
      deliverables: [],
      tasks: []
    })
    
    setShowPhaseForm(false)
  }

  const removePhase = (index: number) => {
    setFormData(prev => ({
      ...prev,
      phases: prev.phases.filter((_, i) => i !== index)
    }))
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="glass-mockup rounded-2xl border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-white">Create Project Template</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 glass-mockup border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                  placeholder="Wedding Photography Package"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as any }))}
                  className="w-full px-3 py-2 glass-mockup border border-white/10 rounded-lg text-white bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                  required
                >
                  {Object.entries(projectTemplateCategories).map(([key, category]) => (
                    <option key={key} value={key} className="bg-gray-900 text-white">
                      {category.icon} {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 glass-mockup border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent resize-none"
                placeholder="Describe this template..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Duration (days)
                </label>
                <input
                  type="number"
                  value={formData.estimatedDuration}
                  onChange={(e) => setFormData(prev => ({ ...prev, estimatedDuration: parseInt(e.target.value) || 7 }))}
                  className="w-full px-3 py-2 glass-mockup border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Team Size
                </label>
                <input
                  type="number"
                  value={formData.recommendedTeamSize}
                  onChange={(e) => setFormData(prev => ({ ...prev, recommendedTeamSize: parseInt(e.target.value) || 1 }))}
                  className="w-full px-3 py-2 glass-mockup border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Difficulty
                </label>
                <select
                  value={formData.difficultyLevel}
                  onChange={(e) => setFormData(prev => ({ ...prev, difficultyLevel: e.target.value as any }))}
                  className="w-full px-3 py-2 glass-mockup border border-white/10 rounded-lg text-white bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                >
                  <option value="beginner" className="bg-gray-900 text-white">Beginner</option>
                  <option value="intermediate" className="bg-gray-900 text-white">Intermediate</option>
                  <option value="advanced" className="bg-gray-900 text-white">Advanced</option>
                </select>
              </div>
            </div>

            {/* Phases Section */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Project Phases</h3>
                <button
                  type="button"
                  onClick={() => setShowPhaseForm(true)}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200"
                >
                  <PlusIcon className="w-4 h-4 mr-1" />
                  Add Phase
                </button>
              </div>

              {/* Existing Phases */}
              {formData.phases.length > 0 && (
                <div className="space-y-2 mb-4">
                  {formData.phases.map((phase, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: phase.color }}
                        />
                        <span className="font-medium">{phase.name}</span>
                        <span className="text-sm text-gray-500">({phase.estimatedDays} days)</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removePhase(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Phase Form */}
              {showPhaseForm && (
                <div className="border border-gray-200 rounded-md p-4 mb-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phase Name *
                      </label>
                      <input
                        type="text"
                        value={currentPhase.name}
                        onChange={(e) => setCurrentPhase(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Planning & Preparation"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Duration (days)
                      </label>
                      <input
                        type="number"
                        value={currentPhase.estimatedDays}
                        onChange={(e) => setCurrentPhase(prev => ({ ...prev, estimatedDays: parseInt(e.target.value) || 1 }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="1"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={currentPhase.description}
                      onChange={(e) => setCurrentPhase(prev => ({ ...prev, description: e.target.value }))}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Phase description..."
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowPhaseForm(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={addPhase}
                      disabled={!currentPhase.name.trim()}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CheckIcon className="w-4 h-4 mr-1 inline" />
                      Add Phase
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags (comma-separated)
              </label>
              <input
                type="text"
                value={formData.tags.join(', ')}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  tags: e.target.value.split(',').map(tag => tag.trim()).filter(Boolean)
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="wedding, photography, professional"
              />
            </div>

            {/* Public Template */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isPublic}
                  onChange={(e) => setFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Make this template public (visible to other users)
                </span>
              </label>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!formData.name.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Template
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  )
}

// Placeholder components for Edit and View modals
function EditTemplateModal({ template, onClose }: {
  template: ProjectTemplate
  onClose: () => void
  onSave: (template: ProjectTemplate) => void
}) {
  // TODO: Implement edit modal
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4">Edit Template: {template.name}</h2>
        <p className="text-gray-600 mb-4">Edit functionality will be implemented here.</p>
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
        >
          Close
        </button>
      </div>
    </div>
  )
}

function ViewTemplateModal({ template, onClose, onEdit, onDuplicate }: {
  template: ProjectTemplate
  onClose: () => void
  onEdit: () => void
  onDuplicate: () => void
}) {
  // TODO: Implement view modal
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl">
        <h2 className="text-lg font-semibold mb-4">Template: {template.name}</h2>
        <p className="text-gray-600 mb-4">{template.description}</p>
        <div className="flex gap-2">
          <button
            onClick={onEdit}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Edit
          </button>
          <button
            onClick={onDuplicate}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            Duplicate
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

function UseTemplateModal({ template, onClose, onUse }: {
  template: ProjectTemplate
  onClose: () => void
  onUse: (projectData: {
    name: string
    clientId?: string
    shootId?: string
    startDate: string
    shootDate?: string
  }) => void
}) {
  const [formData, setFormData] = useState({
    name: `${template.name} Project`,
    clientId: '',
    shootId: '',
    startDate: new Date().toISOString().split('T')[0],
    shootDate: ''
  })

  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim()) return

    setIsLoading(true)
    try {
      await onUse({
        name: formData.name,
        clientId: formData.clientId || undefined,
        shootId: formData.shootId || undefined,
        startDate: formData.startDate,
        shootDate: formData.shootDate || undefined
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="glass-mockup rounded-2xl border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-semibold text-white">Use Template</h2>
              <p className="text-gray-400 text-sm mt-1">{template.name}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Template Info */}
            <div className="glass-mockup rounded-lg border border-white/10 p-4">
              <h3 className="text-white font-medium mb-2">Template Overview</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center gap-2 text-gray-400">
                  <CalendarIcon className="w-4 h-4" />
                  <span>{template.estimatedDuration} days</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <UsersIcon className="w-4 h-4" />
                  <span>{template.recommendedTeamSize} team members</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <ClockIcon className="w-4 h-4" />
                  <span>{template.phases.length} phases</span>
                </div>
              </div>
              <p className="text-gray-400 text-sm mt-3">{template.description}</p>
            </div>

            {/* Project Details */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Project Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 glass-mockup rounded-lg border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                  placeholder="Enter project name"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                    className="w-full px-4 py-3 glass-mockup rounded-lg border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Shoot Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={formData.shootDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, shootDate: e.target.value }))}
                    className="w-full px-4 py-3 glass-mockup rounded-lg border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Client ID (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.clientId}
                    onChange={(e) => setFormData(prev => ({ ...prev, clientId: e.target.value }))}
                    className="w-full px-4 py-3 glass-mockup rounded-lg border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                    placeholder="Link to existing client"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Shoot ID (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.shootId}
                    onChange={(e) => setFormData(prev => ({ ...prev, shootId: e.target.value }))}
                    className="w-full px-4 py-3 glass-mockup rounded-lg border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                    placeholder="Link to existing shoot"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t border-white/10">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-300 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!formData.name.trim() || isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isLoading ? 'Creating...' : 'Create Project'}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  )
} 